/**
 * Created by Ivo Mehide <ivo@degeetia.ee> on 24.05.17.
 */

import React from "react";
import PropTypes from "prop-types";
import t from "../../trans";
import {FormGroup, Modal, Select} from "../../components/Bootstrap";

class SelectRoleModal extends React.Component {
    static propTypes = {
        /**
         * Active person role ID
         */
        activeRole: PropTypes.oneOfType([PropTypes.string,PropTypes.number]),
        /**
         * Array of person roles:
         *      [[id, organizationName, roleName], ...]
         */
        roles: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            organizationName: PropTypes.string,
            roleName: PropTypes.string
        })).isRequired,
        /**
         * "Is default" checkbox value
         */
        isDefault: PropTypes.bool,
        /**
         * Show error status if needed
         */
        showValidationState: PropTypes.bool,
        /**
         * Is successful role selection required
         */
        isRequired: PropTypes.bool,
        /**
         * Callback called when role or checkbox value was changed. Field name is "role" or "isDefault"
         * onChange(fieldName, value)
         */
        onChange: PropTypes.func.isRequired,
        /**
         * Callback called when user closes the modal window. Parameter selectClicked is boolean indicating if the
         * form is closed because of clicking to the "Select" button or not.
         * onClose(selectClicked)
         */
        onClose: PropTypes.func.isRequired
    };

    static defaultProps = {
        isRequired: false
    };

    constructor(props) {
        super(props);
        this.state = {
            groupedOptions: {},
            selectedOrg: null,
            selectedRoleId: null
        };
        this.onClose = this.onClose.bind(this);
        this.onSelect = this.onSelect.bind(this);
        this.setSelectedOrg = this.setSelectedOrg.bind(this);
    }

    componentDidMount() {
        this.setRoleOptions(this.props.roles);
    }

    setRoleOptions(roles) {
        if (!roles) return;

        const grouped = this.groupByOrganization(roles);
        this.setState({groupedOptions: grouped});

        // Populate default choice with user's previous choice, if available.
        const previousChoiceRoleId = this.props.activeRole;
        if (previousChoiceRoleId) {
            const previousChoiceRole = this.findRoleAndOrgName(grouped, previousChoiceRoleId)
            if (previousChoiceRole) {
                this.setState({
                    selectedOrg: previousChoiceRole.organization,
                    selectedRoleId: previousChoiceRole.role.value
                });
                return;
            }
        }

        //If user only has roles with one org, preselect the org
        const uniqueOrgs = Object.keys(grouped);
        if (uniqueOrgs.length === 1) {
            this.setState({selectedOrg: uniqueOrgs[0]});
        }
    }

    groupByOrganization(roles) {
        return roles.reduce((acc, item) => {
            const orgName = item.organizationName;
            if (!acc[orgName]) acc[orgName] = [];

            acc[orgName].push({
                label: item.roleName,
                value: item.id
            });
            return acc;
        }, {});
    }

    findRoleAndOrgName(groupedRoles, roleId) {
        for (const [orgName, rolesArray] of Object.entries(groupedRoles)) {
            const foundRole = rolesArray.find(role => role.value === roleId);

            if (foundRole) {
                return {
                    organization: orgName,
                    role: foundRole
                };
            }
        }
        return null;
    }

    setSelectedOrg(field, value) {
        const groupedOptions = this.state.groupedOptions;
        let selectedOrg = value;
        let newState = {
            selectedOrg: selectedOrg,
            selectedRoleId: null // Reset role when Org changes
        };

        if (selectedOrg && groupedOptions[selectedOrg]) {
            const rolesForThisOrg = groupedOptions[selectedOrg];
            if (rolesForThisOrg.length === 1) {
                const roleId = rolesForThisOrg[0].value;
                newState.selectedRoleId = roleId;
                this.props.onChange("activeRole", roleId);
            } else {
                this.props.onChange("activeRole", null);
            }
        } else {
            this.props.onChange("activeRole", null);
        }
        this.setState(newState);
    }

    setSelectedRole = (name, value) => {
        this.setState({selectedRoleId: value});
        // Let the parent component know about the role change
        this.props.onChange("activeRole", value);
    }

    onClose() {
        if (this.props.isRequired && this.props.activeRole == null) return;
        this.props.onClose(false);
    }


    onSelect() {
        if (this.props.activeRole == null) return;
        this.props.onClose(true);
    }

    getRoleValidationState() {
        if (!this.props.showValidationState) return "";
        if (this.props.activeRole == null) return "error";
        return "";
    }

    render() {
        /*
         <FormGroup>
            <Checkbox label={t("Set as default")} id="isDefault" name="isDefault" value={this.props.isDefault} onChange={this.props.onChange}/>
         </FormGroup>
         */

        const rawRoles = (this.state.selectedOrg && this.state.groupedOptions[this.state.selectedOrg]) || [];
        const formattedRoles = rawRoles.map(role => [role.value, role.label]);

        return (
            <Modal modalClass="inmodal fade" modalAttrib={{"data-backdrop": "static", "data-keyboard": "false"}}
                   id="selectRole" tabIndex="-1" role="dialog" aria-hidden="true" onClose={this.onClose}>
                <Modal.Content>
                    <Modal.Header>
                        <button type="button" className="close" data-dismiss="modal"><span
                            aria-hidden="true">&times;</span><span className="sr-only">{t("Close")}</span></button>
                        <h4 className="modal-title">{t("Select Role")}</h4>
                    </Modal.Header>
                    <Modal.Body>
                        <form className="form-horizontal">
                            <FormGroup label={t("Organization")} inputId="activeOrganization"
                                       helpText={t("Select organization")}
                                       validationStatus={this.getRoleValidationState()}>
                                <Select id="orgSelect" name="orgSelect"
                                        value={this.state.selectedOrg || ""}
                                        onChange={this.setSelectedOrg}
                                        options={Object.keys(this.state.groupedOptions).map((orgName) => {
                                            return {value: orgName, label: orgName}
                                        })}
                                />
                            </FormGroup>
                            <FormGroup label={t("Role")} inputId="activeRole" helpText={t("Select role")}
                                       validationStatus={this.getRoleValidationState()}>
                                <Select id="roleSelect" name="roleSelect"
                                        value={this.state.selectedRoleId}
                                        options={formattedRoles}
                                        onChange={this.setSelectedRole}
                                        disabled={!this.state.selectedOrg}
                                />
                            </FormGroup>
                        </form>
                    </Modal.Body>
                    <Modal.Footer>
                        <button type="button" className="btn btn-white" data-dismiss="modal">{t("Cancel")}</button>
                        <button type="button" className="btn btn-primary" onClick={this.onSelect}
                                disabled={this.props.activeRole == null}>{t("Select")}</button>
                    </Modal.Footer>
                </Modal.Content>
            </Modal>
        );
    }
}

export default SelectRoleModal;
